import React, { useCallback, useEffect, useMemo, useState } from 'react';
import authService from '../../api/authService';
import {
  actionKeys,
  availablePages,
  buildActionPermissions,
  buildCustomPermissions,
  buildTableActions,
  specialPermissions,
  tableActionKeys
} from '../../constants/accessControl';

const createInitialForm = () => ({
  id: '',
  name: '',
  description: '',
  readOnly: false,
  allowedPages: ['customers'],
  actionPermissions: buildActionPermissions(false),
  customPermissions: buildCustomPermissions(false),
  tableActions: buildTableActions(false)
});

const allowedPageKeys = availablePages.map((page) => page.key);

const RolesPage = () => {
  const [profile, setProfile] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [formData, setFormData] = useState(createInitialForm());
  const [message, setMessage] = useState('');
  const [tableActionOptions, setTableActionOptions] = useState(tableActionKeys);

  const isOwner = profile?.role === 'owner';

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      const meRes = await authService.me();
      if (meRes.success) {
        setProfile(meRes.userInfo);
        if (meRes.userInfo?.role !== 'owner') {
          setRoles([]);
          return;
        }
      }

      const rolesRes = await authService.queryRoles({
        page: 1,
        limit: 100,
        search,
        status
      });

      if (rolesRes.success) {
        setRoles(rolesRes.data || []);
        setTableActionOptions(rolesRes.tableActionOptions || tableActionKeys);
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const resetForm = () => {
    setFormData(createInitialForm());
    setMessage('');
  };

  const handleAllowedPageToggle = (pageKey) => {
    setFormData((prev) => ({
      ...prev,
      allowedPages: prev.allowedPages.includes(pageKey)
        ? prev.allowedPages.filter((item) => item !== pageKey)
        : [...prev.allowedPages, pageKey]
    }));
  };

  const handleActionPermissionChange = (feature, action, checked) => {
    setFormData((prev) => ({
      ...prev,
      actionPermissions: {
        ...prev.actionPermissions,
        [feature]: {
          ...prev.actionPermissions[feature],
          [action]: checked
        }
      }
    }));
  };

  const handleCustomPermissionChange = (permissionKey, checked) => {
    setFormData((prev) => ({
      ...prev,
      customPermissions: {
        ...prev.customPermissions,
        [permissionKey]: checked
      }
    }));
  };

  const handleTableActionChange = (feature, action, checked) => {
    setFormData((prev) => ({
      ...prev,
      tableActions: {
        ...prev.tableActions,
        [feature]: {
          ...prev.tableActions?.[feature],
          [action]: checked
        }
      }
    }));
  };

  const handleReadOnlyChange = (checked) => {
    setFormData((prev) => ({
      ...prev,
      readOnly: checked,
      actionPermissions: buildActionPermissions(checked, prev.actionPermissions),
      customPermissions: buildCustomPermissions(checked, prev.customPermissions),
      tableActions: buildTableActions(checked, prev.tableActions)
    }));
  };

  const handleEdit = (role) => {
    const nextAllowedPages = (role.allowedPages || []).filter((pageKey) => allowedPageKeys.includes(pageKey));

    setFormData({
      id: role._id,
      name: role.name || '',
      description: role.description || '',
      readOnly: !!role.readOnly,
      allowedPages: nextAllowedPages.length ? nextAllowedPages : ['customers'],
      actionPermissions: buildActionPermissions(!!role.readOnly, role.actionPermissions || {}),
      customPermissions: buildCustomPermissions(!!role.readOnly, role.customPermissions || {}),
      tableActions: buildTableActions(!!role.readOnly, role.tableActions || {})
    });
    setMessage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (formData.allowedPages.length === 0) {
      setMessage('Please select at least one allowed page.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formData.name,
        description: formData.description,
        readOnly: formData.readOnly,
        allowedPages: formData.allowedPages,
        actionPermissions: formData.actionPermissions,
        customPermissions: formData.customPermissions,
        tableActions: formData.tableActions
      };

      const res = formData.id
        ? await authService.updateRole(formData.id, payload)
        : await authService.createRole(payload);

      if (res.success) {
        setMessage(`Role ${formData.id ? 'updated' : 'created'} successfully.`);
        resetForm();
        await loadPage();
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusToggle = async (role) => {
    try {
      await authService.updateRole(role._id, { isActive: !role.isActive });
      await loadPage();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const allowedPageBadges = useMemo(
    () => availablePages.reduce((acc, page) => ({ ...acc, [page.key]: page.label }), {}),
    []
  );

  if (loading && !profile) {
    return <div className="p-5 text-center">Loading roles...</div>;
  }

  if (!isOwner) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning mb-0">Only the workspace owner can manage roles.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-700 mb-1">Roles & Permissions</h4>
          <p className="text-muted small mb-0">Create reusable role templates with module access and special permissions like upload files, edit sales, or payment edit.</p>
        </div>
        <button className="btn btn-outline-secondary" onClick={resetForm}>New Role</button>
      </div>

      {message && (
        <div className={`alert ${message.toLowerCase().includes('success') || message.toLowerCase().includes('created') || message.toLowerCase().includes('updated') ? 'alert-success' : 'alert-info'}`}>
          {message}
        </div>
      )}

      <div className="row g-4">
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-700 mb-3">{formData.id ? 'Edit Role' : 'Create Role'}</h6>
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Role Name</label>
                    <input className="form-control" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea className="form-control" rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                  </div>
                </div>

                <div className="form-check form-switch mt-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="role-read-only"
                    checked={formData.readOnly}
                    onChange={(e) => handleReadOnlyChange(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="role-read-only">Read only role</label>
                </div>

                <div className="mt-4">
                  <label className="form-label fw-600">Allowed Pages</label>
                  <div className="row g-2">
                    {availablePages.map((page) => (
                      <div className="col-md-6" key={page.key}>
                        <label className="border rounded p-2 d-flex align-items-center gap-2 w-100">
                          <input type="checkbox" checked={formData.allowedPages.includes(page.key)} onChange={() => handleAllowedPageToggle(page.key)} />
                          <span>{page.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="form-label fw-600">Page Permissions</label>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Module</th>
                          {actionKeys.map((action) => (
                            <th key={action} className="text-capitalize">{action}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {availablePages.map((page) => (
                          <tr key={page.key}>
                            <td>{page.label}</td>
                            {actionKeys.map((action) => (
                              <td key={action}>
                                <input
                                  type="checkbox"
                                  checked={!!formData.actionPermissions?.[page.key]?.[action]}
                                  disabled={formData.readOnly && action !== 'view'}
                                  onChange={(e) => handleActionPermissionChange(page.key, action, e.target.checked)}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="form-label fw-600">Special Permissions</label>
                  <div className="row g-2">
                    {specialPermissions.map((permission) => (
                      <div className="col-md-6" key={permission.key}>
                        <label className="border rounded p-2 d-flex align-items-center gap-2 w-100">
                          <input
                            type="checkbox"
                            checked={!!formData.customPermissions?.[permission.key]}
                            disabled={formData.readOnly}
                            onChange={(e) => handleCustomPermissionChange(permission.key, e.target.checked)}
                          />
                          <span>{permission.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="form-label fw-600">Table Actions</label>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Module</th>
                          {tableActionOptions.map((action) => (
                            <th key={action} className="text-capitalize">{action}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {availablePages.map((page) => (
                          <tr key={`${page.key}-table-actions`}>
                            <td>{page.label}</td>
                            {tableActionOptions.map((action) => (
                              <td key={action}>
                                <input
                                  type="checkbox"
                                  checked={!!formData.tableActions?.[page.key]?.[action]}
                                  disabled={formData.readOnly && action !== 'view'}
                                  onChange={(e) => handleTableActionChange(page.key, action, e.target.checked)}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="d-flex gap-2 mt-3">
                  <button className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (formData.id ? 'Update Role' : 'Create Role')}
                  </button>
                  {formData.id && (
                    <button type="button" className="btn btn-light" onClick={resetForm}>
                      Cancel Edit
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-md-8">
                  <input className="form-control" placeholder="Search by role name or description" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Role</th>
                      <th>Pages</th>
                      <th>Status</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">No roles found.</td>
                      </tr>
                    ) : roles.map((role) => (
                      <tr key={role._id}>
                        <td>
                          <div className="fw-600">{role.name}</div>
                          <div className="small text-muted">{role.description || 'No description'}</div>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {(role.allowedPages || []).map((pageKey) => (
                              <span key={pageKey} className="badge bg-light text-dark border">
                                {allowedPageBadges[pageKey] || pageKey}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${role.isActive ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                            {role.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {role.readOnly && <div className="small text-muted mt-1">Read only</div>}
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-2">
                            <button className="btn btn-sm btn-light" onClick={() => handleEdit(role)}>Edit</button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleStatusToggle(role)}>
                              {role.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPage;
